import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";

import {
  AccessDeniedScreen,
  CasePageReady,
  LoadingOverlay,
  useAccessValidation,
} from "../Providers/ProtectedRoute";
import { fetchAllPages } from "../Redux/caseData/caseDataSlice";
import { fetchAllPages_New, GLOBAL_PAGES_CACHE_DURATION } from "../Redux/pages/pagesSlice";
import { getCaseId } from "../Utils/helper";

import {
  caseAllowsRoute,
  getLoadedGlobalPages,
  isAlwaysAllowedRoute,
  routeIsSidebarRestricted,
} from "./routeAccessPolicy.mjs";

const getCaseIdForRoute = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  return parts[2] || getCaseId();
};

const ReadyCaseOutlet = () => (
  <>
    <Outlet />
    <CasePageReady />
  </>
);

const ReadyAccessDenied = () => (
  <>
    <AccessDeniedScreen />
    <CasePageReady />
  </>
);

const PageLoadError = ({ onRetry }) => (
  <>
    <div className="case-loading-screen">
      <div className="case-loading-popup p-3" role="alert">
        <p>Unable to load the pages for this case. Please try again.</p>
        <button type="button" className="btn btn-primary mt-2" onClick={onRetry}>
          Retry Loading Pages
        </button>
      </div>
    </div>
    <CasePageReady />
  </>
);

const CaseTypeRouteGuard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { loadingCase, loadingIds } = useAccessValidation();
  const globalPagesState = useSelector((state) => state.allPages);
  const casePagesState = useSelector((state) => state.caseData);
  // Refreshing the shared catalog must preserve the mounted page and its modals.
  const globalPages = getLoadedGlobalPages(globalPagesState);
  const alwaysAllowed = isAlwaysAllowedRoute(location.pathname);
  const globalPagesStale =
    !globalPagesState?.lastFetched ||
    Date.now() - globalPagesState.lastFetched > GLOBAL_PAGES_CACHE_DURATION;

  useEffect(() => {
    const status = globalPagesState?.status || "idle";
    if (!alwaysAllowed && (status === "idle" || (status === "succeeded" && globalPagesStale))) {
      dispatch(fetchAllPages_New());
    }
  }, [alwaysAllowed, dispatch, globalPagesStale, globalPagesState?.status]);

  const isSidebarRestricted = useMemo(
    () => routeIsSidebarRestricted(location.pathname, globalPages),
    [globalPages, location.pathname]
  );
  const routeCaseId = isSidebarRestricted ? getCaseIdForRoute(location.pathname) : null;
  const normalizedCaseId = routeCaseId ? String(routeCaseId) : null;
  const hasMatchingCasePages = normalizedCaseId && casePagesState?.pagesCaseId === normalizedCaseId;

  useEffect(() => {
    if (!isSidebarRestricted || !normalizedCaseId) return;

    const requestAlreadyRunning = hasMatchingCasePages && casePagesState?.pagesStatus === "loading";
    const pagesReady = hasMatchingCasePages && casePagesState?.pagesStatus === "succeeded";
    const requestFailed = hasMatchingCasePages && casePagesState?.pagesStatus === "failed";

    if (!requestAlreadyRunning && !pagesReady && !requestFailed) {
      dispatch(fetchAllPages(normalizedCaseId));
    }
  }, [
    casePagesState?.pagesStatus,
    dispatch,
    hasMatchingCasePages,
    isSidebarRestricted,
    normalizedCaseId,
  ]);

  if (alwaysAllowed) return <ReadyCaseOutlet />;

  if (globalPagesState?.status === "failed") {
    return <PageLoadError onRetry={() => dispatch(fetchAllPages_New())} />;
  }
  if (isSidebarRestricted === null) {
    return (
      <LoadingOverlay
        caseData={loadingCase}
        clientId={loadingIds.clientId}
        caseId={loadingIds.caseId}
      />
    );
  }

  if (!isSidebarRestricted) return <ReadyCaseOutlet />;
  if (!normalizedCaseId) return <ReadyAccessDenied />;

  if (
    !hasMatchingCasePages ||
    casePagesState?.pagesStatus === "idle" ||
    casePagesState?.pagesStatus === "loading"
  ) {
    return (
      <LoadingOverlay
        caseData={loadingCase}
        clientId={loadingIds.clientId}
        caseId={loadingIds.caseId}
      />
    );
  }

  if (casePagesState?.pagesStatus === "failed") {
    return <PageLoadError onRetry={() => dispatch(fetchAllPages(normalizedCaseId, true))} />;
  }

  return caseAllowsRoute(location.pathname, casePagesState?.pages) ? (
    <ReadyCaseOutlet />
  ) : (
    <ReadyAccessDenied />
  );
};

export default CaseTypeRouteGuard;
